import {
  Map,
  MapMarker,
  useMap,
} from "@scandotcom/react/dist/esm/components/Map";
import { LngLat, type LngLatBoundsLike } from "mapbox-gl";
import * as React from "react";
import RestClient from "../lib/RestClient";
import { useIsGoogleScriptsLoaded } from "../shared/GoogleScriptsContext";

import { LocationInput } from "../Components/Search/LocationInput";
import {
  ScanningLocation,
  ScanningLocationResponse,
} from "../types/scan/ScanningLocation";
import { LocationPopup } from "./LocationPopup";
import { LocationPopupContent } from "./LocationPopupContent";
import { formatCurrency } from "@scandotcom/react";

// For Google APIs
const UKBoundsLiteral = {
  south: 49.620288,
  west: -8.336287,
  north: 59.091983,
  east: 2.223733,
};

// For Mapbox
const UKBounds = [
  { lat: UKBoundsLiteral.south, lng: UKBoundsLiteral.west },
  { lat: UKBoundsLiteral.north, lng: UKBoundsLiteral.east },
] satisfies LngLatBoundsLike;

type Props = {
  scanType?: string;
  showPrices?: boolean;
};

export const LocationMapSearch = ({ scanType, showPrices }: Props) => {
  const inputRef = React.useRef<null | HTMLInputElement>(null);

  const isLoaded = useIsGoogleScriptsLoaded();

  const [locations, setLocations] = React.useState<ScanningLocation[]>([]);

  const [selectedLocation, setSelectedLocation] =
    React.useState<ScanningLocation | null>(null);
  const { mapRef } = useMap([]);

  // Fetch all locations on component mount
  React.useEffect(() => {
    fetchLocations({ count: 600 });
  }, []);

  const performSearch = async () => {
    const location = inputRef.current?.value;

    if (!location || !isLoaded) {
      return;
    }

    const geocoder = new google.maps.Geocoder();
    const geocodeResponse = await geocoder
      .geocode({
        address: location,
        bounds: UKBoundsLiteral,
      })
      .catch((): google.maps.GeocoderResponse => {
        return { results: [] };
      });

    const result = geocodeResponse.results[0];

    if (!result) {
      return;
    }

    const searchLocation = new LngLat(
      result.geometry.location.lng(),
      result.geometry.location.lat()
    );

    setSelectedLocation(null);
    mapRef.current?.flyTo({
      center: searchLocation,
      zoom: 9,
      padding: { top: 10, bottom: 10, left: 10, right: 10 },
    });
  };

  const fetchLocations: (params?: {
    count?: number;
    radius?: number;
    coords?: LngLat;
  }) => Promise<ScanningLocation[] | null> = async ({
    count = 20,
    radius = 20,
    coords,
  } = {}) => {
    const url = new URL("/api/v1/scanning_locations", window.location.origin);
    if (coords) {
      url.searchParams.append("lat", `${coords.lat}`);
      url.searchParams.append("lng", `${coords.lng}`);
    }
    if (scanType) {
      url.searchParams.append("modality", scanType);
    }
    url.searchParams.append("count", `${count}`);
    url.searchParams.append("radius", `${radius}`);

    const response = await RestClient.get<unknown>(url.toString()).then(
      ({ data }) => ScanningLocationResponse.decode(data)
    );

    if (response._tag === "Right") {
      setLocations(response.right.locations);
      return response.right.locations;
    }
    console.warn(
      "Got unexpected data from scanning location api",
      response.left
    );
    return null;
  };

  const popup = selectedLocation ? (
    <LocationPopup
      onClose={() => {
        mapRef.current?.flyTo({ zoom: 9 });
        setSelectedLocation(null);
      }}
      longitude={selectedLocation.longitude}
      latitude={selectedLocation.latitude}
    >
      <LocationPopupContent location={selectedLocation} />
    </LocationPopup>
  ) : null;

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          performSearch();
        }}
        className="mx-auto mb-10 flex max-w-screen-sm items-center gap-4 rounded-xl border border-neutral-300 bg-white p-2.5"
      >
        <div className="grow">
          <LocationInput ref={inputRef} inputClassName="flex-row-reverse" />
        </div>

        <button type="submit" className="button button-primary">
          Search
        </button>
      </form>
      <div>
        <Map
          mapboxAccessToken={window.AppConfig.MAPBOX_ACCESS_TOKEN}
          className="h-128 overflow-hidden rounded-lg lg:col-span-2"
          initialViewState={{
            bounds: UKBounds,
            padding: { top: 20, bottom: 20, left: 20, right: 20 },
          }}
          ref={mapRef}
        >
          {locations.map((location) => (
            <MapMarker
              price={
                showPrices && location.from_price
                  ? formatCurrency(location.from_price / 100)
                  : undefined
              }
              key={location.id}
              latitude={location.latitude}
              longitude={location.longitude}
              onClick={() => {
                setSelectedLocation(location);
                mapRef.current?.flyTo({
                  center: [location.longitude, location.latitude],
                  padding: { top: 10, bottom: 370, left: 10, right: 10 },
                  zoom: 12,
                });
              }}
            />
          ))}

          {popup}
        </Map>
      </div>
    </div>
  );
};
